import { useCallback, useEffect, useState } from "react";
import type {
  ModelEndpointSummary,
  ModelProtocol,
  ModelTestResult,
  Role,
  UserSummary
} from "@vocab1600/shared";
import { modelProtocolLabels, roleLabels } from "@vocab1600/shared";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "./api.js";

const ALL_ROLES: Role[] = ["super_admin", "organization_admin", "teacher", "student"];

function errorText(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.fields?.length) return cause.fields.map((f) => f.message).join("；");
    if (cause.detail) return cause.detail;
    // 兜底时带上状态码与错误码，避免只显示一个无信息量的 "FastifyError"
    return `请求失败（HTTP ${cause.status} ${cause.code}）`;
  }
  return cause instanceof Error ? cause.message : "操作失败";
}

/** 生成一个便于口头转达但仍有足够强度的初始密码 */
function suggestPassword(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  return `Vs${btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "x")}`;
}

/* ========================================================================== */
/* 用户管理                                                                    */
/* ========================================================================== */

interface UserFormState {
  id?: string;
  email: string;
  displayName: string;
  password: string;
  roles: Role[];
  active: boolean;
}

const emptyUserForm = (): UserFormState => ({
  email: "",
  displayName: "",
  password: suggestPassword(),
  roles: ["student"],
  active: true
});

export function UserAdmin({ currentUser }: { currentUser: UserSummary }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<UserFormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try { setUsers((await api.users()).users); }
    catch (cause) { setError(errorText(cause)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true); setError(""); setNotice("");
    try {
      if (form.id) {
        await api.updateUser(form.id, { displayName: form.displayName, roles: form.roles, active: form.active });
        if (form.password) {
          await api.resetPassword(form.id, form.password);
          setNotice(`已更新「${form.displayName}」并重置密码，请转告新密码：${form.password}`);
        } else {
          setNotice(`已更新「${form.displayName}」`);
        }
      } else {
        await api.createUser({
          email: form.email,
          displayName: form.displayName,
          password: form.password,
          roles: form.roles,
          mustChangePassword: true
        });
        setNotice(`已创建「${form.displayName}」，初始密码：${form.password}（请转告本人并要求首次登录后修改）`);
      }
      setForm(null);
      await load();
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  }

  async function toggleActive(user: UserSummary) {
    setError(""); setNotice("");
    try { await api.updateUser(user.id, { active: !user.active }); await load(); }
    catch (cause) { setError(errorText(cause)); }
  }

  async function remove(user: UserSummary) {
    if (!window.confirm(`确定删除「${user.displayName}」？该账号的学习记录会一并删除，且无法恢复。`)) return;
    setError(""); setNotice("");
    try { await api.deleteUser(user.id); setNotice(`已删除「${user.displayName}」`); await load(); }
    catch (cause) { setError(errorText(cause)); }
  }

  const filtered = (users ?? []).filter((user) =>
    `${user.displayName}${user.email}${user.roles.join()}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  return <div className="page">
    <header className="page-header">
      <span className="eyebrow dark">ADMIN</span>
      <h1>用户管理</h1>
      <p>创建、编辑、停用与删除账号，并分配角色。停用或重置密码会立即吊销该用户的全部登录会话。</p>
    </header>

    <div className="toolbar">
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索姓名、邮箱或角色" />
      <span>{filtered.length} / {users?.length ?? 0} 人</span>
      <button className="primary-button compact" onClick={() => { setForm(emptyUserForm()); setNotice(""); setError(""); }}>
        + 新建用户
      </button>
    </div>

    {error && <div className="form-error block">{error}</div>}
    {notice && <div className="form-notice">{notice}</div>}

    <section className="panel">
      {users === null ? <p className="muted">正在加载…</p> : filtered.length === 0 ? (
        <p className="muted">没有匹配的用户。</p>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>姓名</th><th>邮箱</th><th>角色</th><th>状态</th><th className="right">操作</th></tr></thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id} className={user.active ? "" : "row-muted"}>
                  <td>
                    <b>{user.displayName}</b>
                    {user.id === currentUser.id && <em className="tag self">当前登录</em>}
                  </td>
                  <td className="mono">{user.email}</td>
                  <td>{user.roles.map((role) => <em key={role} className="tag">{roleLabels[role]}</em>)}</td>
                  <td>{user.active ? <span className="pill ok">启用</span> : <span className="pill off">已停用</span>}</td>
                  <td className="right actions">
                    {user.roles.includes("student") && (
                      <button className="link-button" onClick={() => navigate(`/analytics?userId=${user.id}`)}>
                        看学情
                      </button>
                    )}
                    <button className="link-button" onClick={() => {
                      setForm({ id: user.id, email: user.email, displayName: user.displayName, password: "", roles: user.roles, active: user.active });
                      setNotice(""); setError("");
                    }}>编辑</button>
                    <button className="link-button" onClick={() => void toggleActive(user)} disabled={user.id === currentUser.id}>
                      {user.active ? "停用" : "启用"}
                    </button>
                    <button className="link-button danger" onClick={() => void remove(user)} disabled={user.id === currentUser.id}>删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>

    {form && (
      <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setForm(null); }}>
        <form className="modal" onSubmit={submit}>
          <h3>{form.id ? "编辑用户" : "新建用户"}</h3>
          <label>邮箱
            <input type="email" value={form.email} required disabled={Boolean(form.id)}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
          </label>
          <label>姓名
            <input value={form.displayName} required maxLength={120}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="张三" />
          </label>
          <label>{form.id ? "重置密码（留空则不修改）" : "初始密码"}
            <div className="input-row">
              <input value={form.password} minLength={form.id ? 0 : 10} required={!form.id}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="至少 10 位" />
              <button type="button" className="ghost-button" onClick={() => setForm({ ...form, password: suggestPassword() })}>随机生成</button>
            </div>
          </label>
          <fieldset className="role-picker">
            <legend>角色（可多选）</legend>
            {ALL_ROLES.map((role) => (
              <label key={role} className="check">
                <input type="checkbox" checked={form.roles.includes(role)}
                  onChange={(e) => setForm({
                    ...form,
                    roles: e.target.checked ? [...form.roles, role] : form.roles.filter((r) => r !== role)
                  })} />
                {roleLabels[role]}
              </label>
            ))}
          </fieldset>
          {form.id && (
            <label className="check">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              账号启用
            </label>
          )}
          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setForm(null)}>取消</button>
            <button className="primary-button" disabled={busy || form.roles.length === 0}>
              {busy ? "保存中…" : "保存"}
            </button>
          </div>
        </form>
      </div>
    )}
  </div>;
}

/* ========================================================================== */
/* 模型管理                                                                    */
/* ========================================================================== */

interface ModelFormState {
  id?: string;
  displayName: string;
  protocol: ModelProtocol;
  apiBaseUrl: string;
  apiKey: string;
  modelName: string;
  enabled: boolean;
  isDefault: boolean;
  temperature: string;
  maxTokens: string;
}

const emptyModelForm = (): ModelFormState => ({
  displayName: "",
  protocol: "openai_compatible",
  apiBaseUrl: "",
  apiKey: "",
  modelName: "",
  enabled: true,
  isDefault: false,
  temperature: "0.3",
  maxTokens: "2048"
});

const PRESETS: Array<{ label: string; protocol: ModelProtocol; baseUrl: string; model: string }> = [
  { label: "OpenAI", protocol: "openai_compatible", baseUrl: "https://api.openai.com/v1", model: "gpt-4o-mini" },
  { label: "Anthropic Claude", protocol: "anthropic_messages", baseUrl: "https://api.anthropic.com/v1", model: "claude-sonnet-4-5" },
  { label: "DeepSeek", protocol: "openai_compatible", baseUrl: "https://api.deepseek.com/v1", model: "deepseek-chat" },
  { label: "通义千问", protocol: "openai_compatible", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1", model: "qwen-plus" },
  { label: "月之暗面 Kimi", protocol: "openai_compatible", baseUrl: "https://api.moonshot.cn/v1", model: "moonshot-v1-8k" },
  { label: "智谱 GLM", protocol: "openai_compatible", baseUrl: "https://open.bigmodel.cn/api/paas/v4", model: "glm-4-plus" }
];

export function ModelAdmin() {
  const [models, setModels] = useState<ModelEndpointSummary[] | null>(null);
  const [form, setForm] = useState<ModelFormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ModelTestResult>>({});

  const load = useCallback(async () => {
    try { setModels((await api.models()).models); }
    catch (cause) { setError(errorText(cause)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true); setError(""); setNotice("");
    const payload = {
      displayName: form.displayName,
      protocol: form.protocol,
      apiBaseUrl: form.apiBaseUrl,
      modelName: form.modelName,
      enabled: form.enabled,
      isDefault: form.isDefault,
      settings: {
        ...(form.temperature ? { temperature: Number(form.temperature) } : {}),
        ...(form.maxTokens ? { maxTokens: Number(form.maxTokens) } : {})
      }
    };
    try {
      if (form.id) {
        await api.updateModel(form.id, { ...payload, ...(form.apiKey ? { apiKey: form.apiKey } : {}) });
        setNotice(`已更新「${form.displayName}」`);
      } else {
        await api.createModel({ ...payload, apiKey: form.apiKey });
        setNotice(`已添加「${form.displayName}」，建议立即点击“测试连通”验证配置`);
      }
      setForm(null);
      await load();
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  }

  async function runTest(model: ModelEndpointSummary) {
    setTesting(model.id); setError("");
    try {
      const result = await api.testModel(model.id);
      setResults((prev) => ({ ...prev, [model.id]: result }));
      await load();
    } catch (cause) { setError(errorText(cause)); }
    finally { setTesting(null); }
  }

  async function remove(model: ModelEndpointSummary) {
    if (!window.confirm(`确定删除模型「${model.displayName}」？`)) return;
    try { await api.deleteModel(model.id); setNotice(`已删除「${model.displayName}」`); await load(); }
    catch (cause) { setError(errorText(cause)); }
  }

  return <div className="page">
    <header className="page-header">
      <span className="eyebrow dark">ADMIN</span>
      <h1>模型管理</h1>
      <p>接入任意数量的大模型，支持 OpenAI 兼容与 Anthropic Messages 两种规格。API Key 以 AES-256-GCM 加密存储，界面只显示掩码。</p>
    </header>

    <div className="toolbar">
      <span>{models?.length ?? 0} 个模型</span>
      <button className="primary-button compact" onClick={() => { setForm(emptyModelForm()); setError(""); setNotice(""); }}>
        + 接入新模型
      </button>
    </div>

    {error && <div className="form-error block">{error}</div>}
    {notice && <div className="form-notice">{notice}</div>}

    <section className="panel">
      {models === null ? <p className="muted">正在加载…</p> : models.length === 0 ? (
        <div className="empty-panel">
          <div>🧠</div>
          <h3>还没有接入任何模型</h3>
          <p>学情分析需要调用大模型生成报告。点击右上角「接入新模型」，填写 API 地址、Key 与模型名即可。系统提供常见服务商的预设，选中后自动填好地址与协议。</p>
        </div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>名称</th><th>协议</th><th>模型</th><th>API 地址</th><th>Key</th><th>状态</th><th className="right">操作</th></tr></thead>
            <tbody>
              {models.map((model) => {
                const result = results[model.id];
                return <tr key={model.id} className={model.enabled ? "" : "row-muted"}>
                  <td>
                    <b>{model.displayName}</b>
                    {model.isDefault && <em className="tag self">默认</em>}
                  </td>
                  <td className="nowrap">{model.protocol === "anthropic_messages" ? "Anthropic" : "OpenAI 兼容"}</td>
                  <td className="mono">{model.modelName}</td>
                  <td className="mono truncate" title={model.apiBaseUrl}>{model.apiBaseUrl}</td>
                  <td className="mono">{model.apiKeyMasked}</td>
                  <td>
                    {testing === model.id ? <span className="pill">测试中…</span>
                      : result ? <span className={`pill ${result.ok ? "ok" : "bad"}`} title={result.message}>
                          {result.ok ? `正常 ${result.latencyMs}ms` : "失败"}
                        </span>
                      : model.lastTestOk === true ? <span className="pill ok">上次正常</span>
                      : model.lastTestOk === false ? <span className="pill bad" title={model.lastError ?? ""}>上次失败</span>
                      : <span className="pill">未测试</span>}
                    {!model.enabled && <span className="pill off">已停用</span>}
                  </td>
                  <td className="right actions">
                    <button className="link-button" onClick={() => void runTest(model)} disabled={testing !== null}>测试连通</button>
                    <button className="link-button" onClick={() => setForm({
                      id: model.id, displayName: model.displayName, protocol: model.protocol,
                      apiBaseUrl: model.apiBaseUrl, apiKey: "", modelName: model.modelName,
                      enabled: model.enabled, isDefault: model.isDefault,
                      temperature: String(model.settings.temperature ?? 0.3),
                      maxTokens: String(model.settings.maxTokens ?? 2048)
                    })}>编辑</button>
                    <button className="link-button danger" onClick={() => void remove(model)}>删除</button>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      )}
      {Object.entries(results).map(([id, result]) => !result.ok && (
        <div key={id} className="form-error block">
          {models?.find((m) => m.id === id)?.displayName}：{result.message}
        </div>
      ))}
    </section>

    {form && (
      <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setForm(null); }}>
        <form className="modal wide" onSubmit={submit}>
          <h3>{form.id ? "编辑模型" : "接入新模型"}</h3>

          {!form.id && (
            <div className="preset-row">
              <span>快速填充：</span>
              {PRESETS.map((preset) => (
                <button key={preset.label} type="button" className="chip ghost" onClick={() => setForm({
                  ...form, displayName: form.displayName || preset.label,
                  protocol: preset.protocol, apiBaseUrl: preset.baseUrl, modelName: preset.model
                })}>{preset.label}</button>
              ))}
            </div>
          )}

          <label>名称（自定义，便于识别）
            <input value={form.displayName} required maxLength={120}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="例如：主力分析模型" />
          </label>

          <label>接口规格
            <select value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value as ModelProtocol })}>
              {(Object.keys(modelProtocolLabels) as ModelProtocol[]).map((key) => (
                <option key={key} value={key}>{modelProtocolLabels[key]}</option>
              ))}
            </select>
          </label>

          <label>API 地址
            <input value={form.apiBaseUrl} required type="url"
              onChange={(e) => setForm({ ...form, apiBaseUrl: e.target.value })}
              placeholder="https://api.example.com/v1" />
            <small className="hint">填到 /v1 即可，系统会自动补全 {form.protocol === "anthropic_messages" ? "/messages" : "/chat/completions"}</small>
          </label>

          <label>API Key{form.id && "（留空则不修改）"}
            <input value={form.apiKey} type="password" autoComplete="off" required={!form.id}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
              placeholder={form.id ? "不修改请留空" : "sk-…"} />
          </label>

          <label>模型名
            <input value={form.modelName} required maxLength={160}
              onChange={(e) => setForm({ ...form, modelName: e.target.value })} placeholder="gpt-4o-mini" />
          </label>

          <div className="field-row">
            <label>温度
              <input value={form.temperature} type="number" step="0.1" min="0" max="2"
                onChange={(e) => setForm({ ...form, temperature: e.target.value })} />
            </label>
            <label>最大输出 tokens
              <input value={form.maxTokens} type="number" min="256" max="32000"
                onChange={(e) => setForm({ ...form, maxTokens: e.target.value })} />
            </label>
          </div>

          <label className="check">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            启用该模型
          </label>
          <label className="check">
            <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
            设为默认（学情分析优先使用）
          </label>

          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setForm(null)}>取消</button>
            <button className="primary-button" disabled={busy}>{busy ? "保存中…" : "保存"}</button>
          </div>
        </form>
      </div>
    )}
  </div>;
}
